// ComplexCartStage.tsx
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { MdDeleteOutline, MdPhone } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../../../store/store";
import {
  clearComplexOrderCart,
  decreaseComplexOrderCartQuantity,
  increaseComplexOrderCartQuantity,
  removeFromComplexOrderCart,
  removeAllByParentId,
} from "../../../../store/slices/complexOrderCartSlice";
import { selectCurrentUser } from "../../../../store/slices/currentUserSlice";
import { selectIsOnline } from "../../../../store/slices/networkSlice";
import { selectCurrentCluster } from "../../../../store/slices/currentClusterSlice";
import { selectCategoryList } from "../../../../store/slices/categoryListSlice";
import { useTheme } from "../../../hooks/useTheme";
import { ComplexOrderItem } from "../../../types/types";
import { saveToStorage } from "../../../utils/functions/localStorage";
import { ROUTES } from "../../../utils/constants";
import styles from "./ComplexCartStage.module.scss";
import { getImageUri } from "../../../utils/functions/getImageUri";
import Button from "../../button/Button";
import { unitTypeToShort } from "../../../utils/functions/enumToText";

interface ComplexCartStageProps {
  onSend: () => void;
  onClose: () => void;
  groupedItems: {
    parents: ComplexOrderItem[];
    childrenMap: Record<string, ComplexOrderItem[]>;
  };
}

export default function ComplexCartStage({
  onSend,
  onClose,
  groupedItems,
}: ComplexCartStageProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { parents, childrenMap } = groupedItems;
  const currentUser = useSelector(selectCurrentUser);
  const currentCluster = useSelector(selectCurrentCluster);
  const isOnline = useSelector(selectIsOnline);
  const categoryList = useSelector(selectCategoryList);

  const complexItems = parents.filter((item) => {
    const category = categoryList.find((c) => c.id === item.categoryId);
    return category?.isComplex;
  });

  const regularItems = parents.filter((item) => {
    const category = categoryList.find((c) => c.id === item.categoryId);
    return !category?.isComplex;
  });

  const handleClearCart = () => {
    dispatch(clearComplexOrderCart());
  };

  const handleCall = () => {
    const phone = currentCluster?.phone_number;
    if (phone) {
      window.location.href = `tel:${phone.replace(/\D/g, "")}`;
    }
  };

  const handleContinueOrLogin = () => {
    if (!currentUser) {
      saveToStorage("afterAction", "complexCart");
      onClose();
      navigate(ROUTES.EMAIL_LOGIN);
      return;
    }
    onSend();
  };

  const renderAttributes = (attributes?: Record<string, any>) => {
    if (!attributes || Object.keys(attributes).length === 0) return null;
    return (
      <div className={styles.attributesList}>
        {Object.entries(attributes).map(([key, val]) => (
          <span key={key} className={styles.attributeItem}>
            {key}: {String(val)}
          </span>
        ))}
      </div>
    );
  };

  const handleRemoveParent = (item: ComplexOrderItem) => {
    const children = childrenMap[item.id] || [];
    if (children.length > 0) {
      dispatch(removeAllByParentId(item.id));
    } else {
      dispatch(removeFromComplexOrderCart(item.id));
    }
  };

  const getUnitDisplay = (unit?: number) => {
    if (unit !== undefined && unit !== -1) {
      return unitTypeToShort(unit);
    }
    return "";
  };

  if (parents.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.title} style={{ color: theme.text }}>
            Проект
          </h2>
        </div>
        <div className={styles.emptyContent}>
          <h3 className={styles.emptyTitle} style={{ color: theme.primary }}>
            Список пуст
          </h3>
          <p
            className={styles.emptyText}
            style={{ color: theme.textSecondary }}
          >
            Добавьте товары в проект, чтобы оформить заявку
          </p>
          <Button
            title="Перейти в каталог"
            onClick={onClose}
            variant="primary"
            fullWidth
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title} style={{ color: theme.text }}>
          Проект
        </h2>
        <button className={styles.clearButton} onClick={handleClearCart}>
          <MdDeleteOutline size={18} color={theme.danger} />
          <span
            className={styles.clearButtonText}
            style={{ color: theme.danger }}
          >
            Очистить
          </span>
        </button>
      </div>

      <div className={styles.itemsScrollView}>
        {complexItems.length > 0 && (
          <>
            <h3 className={styles.sectionTitle} style={{ color: theme.text }}>
              Состав заказа
            </h3>
            {complexItems.map((item: ComplexOrderItem) => {
              const children = childrenMap[item.id] || [];
              const hasChildren = children.length > 0;
              const isFixedPrice = item.price > 0;
              const childrenTotal = children.reduce(
                (sum, child) => sum + child.total,
                0,
              );
              const displayPrice = isFixedPrice ? item.price : childrenTotal;
              const unitDisplay = getUnitDisplay(item.unit);

              return (
                <div key={item.id} className={styles.complexGroup}>
                  <div
                    className={styles.complexItemCard}
                    style={{
                      backgroundColor: theme.card,
                      borderColor: theme.border,
                    }}
                  >
                    <img
                      className={styles.complexItemImage}
                      src={`${getImageUri()}${item.image}`}
                      alt={item.name}
                    />
                    <h4
                      className={styles.complexItemName}
                      style={{ color: theme.text }}
                    >
                      {item.name}
                    </h4>
                    {renderAttributes(item.attributes)}
                    {displayPrice > 0 && (
                      <span
                        className={styles.complexItemPrice}
                        style={{ color: theme.primary }}
                      >
                        {Number(displayPrice).toLocaleString("ru-RU")} ₽
                        {unitDisplay && ` / ${unitDisplay}`}
                      </span>
                    )}
                    <div className={styles.complexItemControls}>
                      <div
                        className={styles.quantityControls}
                        style={{ borderColor: theme.border }}
                      >
                        <button
                          className={styles.quantityButton}
                          onClick={() =>
                            dispatch(decreaseComplexOrderCartQuantity(item.id))
                          }
                        >
                          <span style={{ color: theme.primary }}>-</span>
                        </button>
                        <span
                          className={styles.quantityText}
                          style={{ color: theme.text }}
                        >
                          {item.quantity}
                        </span>
                        <button
                          className={styles.quantityButton}
                          onClick={() =>
                            dispatch(increaseComplexOrderCartQuantity(item.id))
                          }
                        >
                          <span style={{ color: theme.success }}>+</span>
                        </button>
                      </div>
                      <button
                        className={styles.removeButton}
                        onClick={() => handleRemoveParent(item)}
                      >
                        <span style={{ color: theme.danger }}>×</span>
                      </button>
                    </div>
                  </div>

                  {hasChildren && (
                    <div className={styles.childrenContainer}>
                      {children.map((child) => {
                        const childUnitDisplay = getUnitDisplay(child.unit);
                        return (
                          <div
                            key={child.id}
                            className={styles.childItemRow}
                            style={{
                              backgroundColor: theme.card,
                              borderColor: theme.border,
                            }}
                          >
                            <img
                              className={styles.childItemImage}
                              src={`${getImageUri()}${child.image}`}
                              alt={child.name}
                            />
                            <span
                              className={styles.childItemName}
                              style={{ color: theme.text }}
                            >
                              {child.name}
                            </span>
                            <span
                              className={styles.childItemQuantity}
                              style={{ color: theme.textSecondary }}
                            >
                              × {child.quantity} {childUnitDisplay}
                            </span>
                            {!isFixedPrice && (
                              <>
                                <div
                                  className={styles.quantityControls}
                                  style={{ borderColor: theme.border }}
                                >
                                  <button
                                    className={styles.quantityButton}
                                    onClick={() =>
                                      dispatch(
                                        decreaseComplexOrderCartQuantity(
                                          child.id,
                                        ),
                                      )
                                    }
                                  >
                                    <span style={{ color: theme.primary }}>
                                      -
                                    </span>
                                  </button>
                                  <span
                                    className={styles.quantityText}
                                    style={{ color: theme.text }}
                                  >
                                    {child.quantity}
                                  </span>
                                  <button
                                    className={styles.quantityButton}
                                    onClick={() =>
                                      dispatch(
                                        increaseComplexOrderCartQuantity(
                                          child.id,
                                        ),
                                      )
                                    }
                                  >
                                    <span style={{ color: theme.success }}>
                                      +
                                    </span>
                                  </button>
                                </div>
                                <button
                                  className={styles.removeButton}
                                  onClick={() =>
                                    dispatch(removeFromComplexOrderCart(child.id))
                                  }
                                >
                                  <span style={{ color: theme.danger }}>×</span>
                                </button>
                              </>
                            )}
                            {isFixedPrice && (
                              <span
                                className={styles.childItemPrice}
                                style={{ color: theme.textSecondary }}
                              >
                                {Number(child.price).toLocaleString("ru-RU")} ₽
                                {childUnitDisplay && ` / ${childUnitDisplay}`}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}

        {regularItems.length > 0 && (
          <>
            <h3 className={styles.sectionTitle} style={{ color: theme.text }}>
              Дополнительно
            </h3>
            {regularItems.map((item: ComplexOrderItem) => {
              const children = childrenMap[item.id] || [];
              const hasChildren = children.length > 0;
              const unitDisplay = getUnitDisplay(item.unit);

              return (
                <div key={item.id} className={styles.complexGroup}>
                  <div
                    className={styles.regularItemRow}
                    style={{
                      backgroundColor: theme.card,
                      borderColor: theme.border,
                    }}
                  >
                    <img
                      className={styles.regularItemImage}
                      src={`${getImageUri()}${item.image}`}
                      alt={item.name}
                    />
                    <div className={styles.regularItemInfo}>
                      <span
                        className={styles.regularItemName}
                        style={{ color: theme.text }}
                      >
                        {item.name}
                      </span>
                      {renderAttributes(item.attributes)}
                      {item.total > 0 && (
                        <span
                          className={styles.regularItemTotal}
                          style={{ color: theme.primary }}
                        >
                          {Number(item.total).toLocaleString("ru-RU")} ₽
                          {unitDisplay && ` / ${unitDisplay}`}
                        </span>
                      )}
                    </div>
                    <div
                      className={styles.quantityControls}
                      style={{ borderColor: theme.border }}
                    >
                      <button
                        className={styles.quantityButton}
                        onClick={() =>
                          dispatch(decreaseComplexOrderCartQuantity(item.id))
                        }
                      >
                        <span style={{ color: theme.primary }}>-</span>
                      </button>
                      <span
                        className={styles.quantityText}
                        style={{ color: theme.text }}
                      >
                        {item.quantity}
                      </span>
                      <button
                        className={styles.quantityButton}
                        onClick={() =>
                          dispatch(increaseComplexOrderCartQuantity(item.id))
                        }
                      >
                        <span style={{ color: theme.success }}>+</span>
                      </button>
                    </div>
                    <button
                      className={styles.removeButton}
                      onClick={() => handleRemoveParent(item)}
                    >
                      <span style={{ color: theme.danger }}>×</span>
                    </button>
                  </div>

                  {hasChildren && (
                    <div className={styles.childrenContainer}>
                      {children.map((child) => {
                        const childUnitDisplay = getUnitDisplay(child.unit);
                        return (
                          <div
                            key={child.id}
                            className={styles.childItemRow}
                            style={{
                              backgroundColor: theme.card,
                              borderColor: theme.border,
                            }}
                          >
                            <img
                              className={styles.childItemImage}
                              src={`${getImageUri()}${child.image}`}
                              alt={child.name}
                            />
                            <span
                              className={styles.childItemName}
                              style={{ color: theme.text }}
                            >
                              {child.name}
                            </span>
                            <span
                              className={styles.childItemQuantity}
                              style={{ color: theme.textSecondary }}
                            >
                              × {child.quantity} {childUnitDisplay}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>

      <div
        className={styles.footer}
        style={{ backgroundColor: `${theme.background}F2` }}
      >
        <div className={styles.buttonsContainer}>
          {isOnline ? (
            <Button
              title={currentUser ? "Продолжить" : "Войти и продолжить"}
              onClick={handleContinueOrLogin}
              variant="primary"
              fullWidth
            />
          ) : (
            <Button
              title="Нет интернета, нажмите чтобы позвонить"
              onClick={handleCall}
              variant="primary"
              icon={<MdPhone size={18} />}
              fullWidth
            />
          )}
          <Button
            title="Продолжить выбор"
            onClick={onClose}
            variant="secondary"
            fullWidth
          />
        </div>
      </div>
    </div>
  );
}